import React, { useState } from 'react';
import { toast } from '../../../../../ui/toast';
import { NoteStructure } from '../../../../../utils/types/JobOpenings';

interface Note {
  id?: string;
  conversationType: 'Email' | 'Phone' | 'Person' | 'Video' | 'Other';
  conversationTime: string;
  subject: string;
  notes: string;
  createdBy?: string;
  createdAt?: string;
}

interface AddNoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (note: NoteStructure) => void;
  candidateName: string;
}

export const AddNoteDialog: React.FC<AddNoteDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  candidateName,
}) => {
  const [formData, setFormData] = useState<NoteStructure>({
    noteType: 'Email',
    noteSubject: '',
    noteBody: '',
    createdAt: new Date().toISOString().slice(0, 16),
  });

  const conversationTypes = [
    { value: 'Email', label: 'Email', icon: '📧', color: 'blue' },
    { value: 'Phone', label: 'Phone Call', icon: '📞', color: 'green' },
    { value: 'Person', label: 'In-Person', icon: '🤝', color: 'purple' },
    { value: 'Video', label: 'Video Call', icon: '🎥', color: 'indigo' },
    { value: 'Other', label: 'Other', icon: '📝', color: 'gray' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {

    e.preventDefault();

    if (!formData.noteSubject.trim() || !formData.noteBody.trim()) {
      toast.error('Please fill in all required fields', {
        position: 'bottom-center',
      });
      return;
    }

    onSave({
      ...formData,
    });

    // Reset form
    setFormData({
      noteType: 'Email',
      noteSubject: '',
      noteBody: '',
      createdAt: new Date().toISOString().slice(0, 16),
    });

    toast.success('Note added successfully', {
      position: 'bottom-center',
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
      green: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100',
      purple: 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100',
      gray: 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100',
    };
    return colors[color] || colors.gray;
  };

  if (!isOpen) return null;

  return (
    <>
      { /* Backdrop */ }
      <div
          className='fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 animate-fadeIn'
          onClick={onClose}
      />

      { /* Dialog */ }
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none'>
        <div
            className='bg-white rounded-2xl shadow-2xl w-full max-w-lg pointer-events-auto animate-slideUp overflow-hidden'
            onClick={(e) => e.stopPropagation()}
        >
          { /* Header */ }
          <div className='bg-gradient-to-r from-green-600 to-emerald-600 px-5 py-4 text-white relative overflow-hidden'>
            <div className='absolute top-0 right-0 w-48 h-48 bg-white opacity-5 rounded-full -mr-24 -mt-24' />
            <div className='absolute bottom-0 left-0 w-32 h-32 bg-white opacity-5 rounded-full -ml-16 -mb-16' />

            <div className='relative flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <div className='w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm'>
                  <span className='text-xl'>📝</span>
                </div>
                <div>
                  <h2 className='text-xl font-bold'>Add Note</h2>
                  <p className='text-green-100 text-xs mt-0.5'>
                    Document conversation with { candidateName }
                  </p>
                </div>
              </div>
              <button
                  onClick={onClose}
                  className='w-8 h-8 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center transition-all duration-200 hover:rotate-90'
                  aria-label='Close dialog'
              >
                <svg
                    className='w-5 h-5'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>
          </div>

          { /* Form */ }
          <form onSubmit={handleSubmit} className='p-5 space-y-4'>
            { /* Conversation Type */ }
            <div>
              <label className='block text-sm font-semibold text-gray-700 mb-2'>
                Conversation Type <span className='text-red-500'>*</span>
              </label>
              <div className='grid grid-cols-5 gap-1.5'>
                { conversationTypes.map((type) => (
                  <button
                      key={type.value}
                      type='button'
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          noteType: type.value as any,
                        }))
                      }
                      className={`p-2 rounded-lg border-2 transition-all duration-200 flex flex-col items-center gap-1 ${
                        formData.noteType === type.value
                          ? `${getColorClasses(type.color)} border-current shadow-md scale-105`
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <span className='text-xl'>{ type.icon }</span>
                    <span className='text-xs font-medium'>{ type.label }</span>
                  </button>
                )) }
              </div>
            </div>

            { /* Conversation Time */ }
            <div>
              <label
                  htmlFor='conversationTime'
                  className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Conversation Date & Time <span className='text-red-500'>*</span>
              </label>
              <div className='relative'>
                <input
                    type='datetime-local'
                    id='createdAt'
                    name='createdAt'
                    value={formData.createdAt}
                    onChange={handleChange}
                    className='w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200 transition-all duration-200 text-sm'
                    required
                />
                <div className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none'>
                  <svg
                      className='w-4 h-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                    />
                  </svg>
                </div>
              </div>
            </div>

            { /* Subject */ }
            <div>
              <label
                  htmlFor='subject'
                  className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Subject <span className='text-red-500'>*</span>
              </label>
              <input
                  type='text'
                  id='noteSubject'
                  name='noteSubject'
                  value={formData.noteSubject}
                  onChange={handleChange}
                  placeholder='e.g., Follow-up on interview availability'
                  className='w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200 transition-all duration-200 text-sm'
                  required
              />
            </div>

            { /* Notes */ }
            <div>
              <label
                  htmlFor='notes'
                  className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Notes <span className='text-red-500'>*</span>
              </label>
              <textarea
                  id='noteBody'
                  name='noteBody'
                  value={formData.noteBody}
                  onChange={handleChange}
                  placeholder='Enter detailed notes about the conversation...'
                  rows={4}
                  className='w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200 transition-all duration-200 resize-none text-sm'
                  required
              />
              <div className='flex justify-between items-center mt-1 text-xs text-gray-500'>
                <span>Be specific and professional</span>
                <span>{ formData.noteBody.length } characters</span>
              </div>
            </div>

            { /* Action Buttons */ }
            <div className='flex gap-2 pt-3 border-t border-gray-200'>
              <button
                  type='button'
                  onClick={onClose}
                  className='flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-all duration-200 hover:shadow-md text-sm'
              >
                Cancel
              </button>
              <button
                  type='submit'
                  className='flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-semibold transition-all duration-200 hover:shadow-lg flex items-center justify-center gap-2 text-sm'
              >
                <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M5 13l4 4L19 7'
                  />
                </svg>
                Save Note
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{ `
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      ` }</style>
    </>
  );
};
